import React from 'react';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { Autocomplete, Box, FilterOptionsState, TextField, Typography } from '@mui/material';

import { TicketListResult } from 'stores/TicketStoreAdapter';

type Props = {
  /** 親候補チケット一覧 */
  tickets: TicketListResult[];
  /** 選択中の親チケットID（null=未選択） */
  selectedTicketId: string | null;
  /** 選択変更ハンドラ */
  onChange: (ticket: TicketListResult | null) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** プレースホルダー */
  placeholder?: string;
};

/**
 * 親チケット選択コンポーネント。
 * Autocomplete ベースで、渡された親候補一覧（onlyParent・同一種別/スコープ・自分自身除外済み）から
 * 管理ID／タイトルで検索して単一選択する（UserSelect/ClientSelect と同流儀）。
 */
const TicketSelect: React.FC<Props> = ({
  tickets,
  selectedTicketId,
  onChange,
  label = '親チケット',
  disabled = false,
  error = false,
  helperText,
  placeholder = '親チケットを検索...',
}) => {
  const filterOptions = (
    options: TicketListResult[],
    state: FilterOptionsState<TicketListResult>,
  ) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;
    return options.filter(
      (t) => t.managementId.toLowerCase().includes(input) || t.title.toLowerCase().includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: TicketListResult,
  ) => {
    const { key, ...rest } = optionProps;
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <AccountTreeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {option.managementId}・{option.typeName}
          </Typography>
        </Box>
      </Box>
    );
  };

  const selected = tickets.find((t) => t.ticketId === selectedTicketId) || null;

  return (
    <Autocomplete
      options={tickets}
      value={selected}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={(option) => `${option.managementId} ${option.title}`}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => option.ticketId === value.ticketId}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      noOptionsText="該当する親チケットがありません"
      sx={{ width: '100%' }}
    />
  );
};

export default TicketSelect;
